import React, { useRef } from "react";
import { MoreOutlined } from "us.icons";
import { $Popover, $Button, $Checkbox } from "us.common/components";
import { EntityType } from "us.collection/constants";
import { StateItemProps } from "us.collection/components/Workflow/Interfaces";
import { WorkflowTag } from "us.collection/components/Workflow/Components";
import {
  getOverriddenColor,
  getSelectedWorkflows,
} from "us.collection/components/Workflow/Functions";
import { connect, ConnectedProps } from "react-redux";
import { useDrag, useDrop } from "react-dnd";
import { useField } from "formik";
import { RootState } from "us.helper/types";
import { ItemMenu } from "../ItemMenu";
import _ from "lodash";

/**
 * @description - The component used to display states content
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3055714493/Manage+State+Categories
 * @return {JSX.Element}
 * @author Ishan Uyoga <ishanud@unicorn-solutions.com>
 * @since 09/11/2024
 */
const StateItem: React.FC<StateItemProps & PropsFromRedux> = (props) => {
  const {
    index,
    stateRecord,
    popoverOpenId,
    isDownloadPopupVisible,
    isSequenceSaving,
    onMove,
    onChangeStateAction,
    onChangePopup,
    onChangeDownloadPopup,
  } = props;

  const ref = useRef<any>(null);
  const [isDraggableField] = useField("isDraggable");
  const [selectedWorkflows, , selectedWorkflowsHelper] =
    useField("selectedWorkflows");
  const [selectionEnabledCategory] = useField("selectionEnabledCategory");

  const {
    id,
    dueInDays,
    dueInType,
    displayName,
    workflowType,
    activeStatus,
    overriddenBy,
    stateGroupId,
    workflowGroupStateId,
  } = stateRecord ?? {};

  const dragId = "ITEM_" + stateGroupId;
  const isDraggable = isDraggableField?.value && !isSequenceSaving

  const [, drag] = useDrag({
    type: dragId,
    item: { index, type: dragId, id: id },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: isDraggable,
  });

  const [{ canDrop, isOver }, drop] = useDrop({
    accept: dragId,
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
    drop(item: any) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      onMove(dragIndex, hoverIndex);

      const scrollElement = document.getElementById("swg-item-" + item?.id);
      if (scrollElement) {
        scrollElement.style.backgroundColor = "#f3fff4";
        setTimeout(() => {
          scrollElement.style.backgroundColor = "";
        }, 2000);
      }

      item.index = hoverIndex;
    },
  });

  drag(drop(ref));
  const isDropEnabled = isOver && canDrop;

  return (
    <div
      ref={ref}
      id={"swg-item-" + id}
      className={
        `d-flex align-items-stretch swg-item ${
          isDraggable ? "swg-item-drag" : ""
        }` + getOverriddenColor(overriddenBy ?? EntityType.BUREAU)
      }
      data-testid="workflow-state"
    >
      {selectionEnabledCategory?.value == stateGroupId && (
        <div className="swg-select-all">
          <$Checkbox
            name={"state-" + id}
            checked={_.some(selectedWorkflows.value, { workflowGroupStateId })}
            className="mr-3"
            onChange={(e: any) => {
              selectedWorkflowsHelper.setValue(
                getSelectedWorkflows(
                  selectedWorkflows.value,
                  stateRecord,
                  e.target.checked,
                  "state"
                )
              );
            }}
          />
        </div>
      )}
      <div
        className="d-flex flex-grow-1 align-items-center swg-item-click"
        style={{
          transform: isDropEnabled
            ? "translateX(10px)"
            : selectionEnabledCategory?.value == stateGroupId
            ? "translateX(30px)"
            : "unset",
          opacity: isDropEnabled
            ? 0.5
            : selectionEnabledCategory?.value == stateGroupId
            ? 0.85
            : "unset",
        }}
      >
        <WorkflowTag workflowType={workflowType} activeStatus={activeStatus} />
        <span className="d-flex align-items-center mr-2 swg-item-days">
          <span className="d-flex align-items-center">
            {dueInDays}
            <small>{dueInType}</small>
          </span>
        </span>
        <span className="d-flex align-items-center swg-item-desc">
          <span>{displayName}</span>
        </span>
      </div>

      <div className="actions">
        <$Popover
          placement="rightTop"
          content={
            <ItemMenu
              isDownloadPopupVisible={isDownloadPopupVisible}
              onCallAction={(action) =>
                onChangeStateAction(action, stateRecord)
              }
              onCancelChildPopup={() => {
                onChangePopup(-1);
                onChangeDownloadPopup(false);
              }}
            />
          }
          destroyTooltipOnHide
          trigger="click"
          open={id == popoverOpenId}
          onOpenChange={(visible: boolean) => {
            onChangePopup(visible ? id : -1);
            onChangeDownloadPopup(false);
          }}
        >
          <$Button
            type="link"
            icon={<MoreOutlined />}
            data-testid="popover-btn"
            size="small"
            onClick={() => {
              onChangePopup(id);
            }}
          />
        </$Popover>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const { Workflow } = state;
  const { isSequenceSaving } = Workflow;
  return {
    isSequenceSaving,
  };
};

const connector = connect(mapStateToProps, {});

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(StateItem);
